import {DateTime} from "luxon";
import type {EBirdObservationsByLocation} from "ebird-mydata-reader";
import {BASE_MONTHS} from "../../helpers/months";
import type {EbirdRegionBounds, EnrichedObservationsByLocation, LocationRegionData} from "../../helpers/types";
import type {
    HotspotExplorerGroup,
    HotspotExplorerIndex,
    HotspotExplorerHotspot,
    HotspotExplorerPeriodFilter,
    HotspotExplorerStats,
    HotspotExplorerSortMode
} from "./types";
import {
    HOTSPOT_EXPLORER_PAGE_PARAM,
    HOTSPOT_EXPLORER_ROUTE,
    LIST_BUILDING_CHECKLIST_DATE,
    LIST_BUILDING_CHECKLIST_DATE_LABEL
} from "./constants";
import {DEFAULT_MAP_CENTER, MAPBOX_ACCESS_TOKEN} from "../map/constants";

type HotspotSource = EBirdObservationsByLocation | EnrichedObservationsByLocation;
const hotspotSortCache = new WeakMap<HotspotExplorerHotspot[], Partial<Record<HotspotExplorerSortMode, HotspotExplorerHotspot[]>>>();
const hotspotStatsCache = new WeakMap<HotspotExplorerHotspot[], Partial<Record<string, HotspotExplorerStats>>>();

const countryDisplayNames = typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], {type: 'region'})
    : null;

export function isListBuildingChecklistDate(date?: string) {
    return date === LIST_BUILDING_CHECKLIST_DATE;
}

export function formatHotspotObservationDay(date?: string) {
    if (!date) return '';
    if (isListBuildingChecklistDate(date)) return LIST_BUILDING_CHECKLIST_DATE_LABEL;

    const parsedDate = DateTime.fromISO(date);
    return parsedDate.isValid ? parsedDate.toFormat('LLLL d, y') : date;
}

const US_STATE_LABELS: Record<string, string> = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
    DC: 'District of Columbia'
};

const CANADA_PROVINCE_LABELS: Record<string, string> = {
    AB: 'Alberta',
    BC: 'British Columbia',
    MB: 'Manitoba',
    NB: 'New Brunswick',
    NL: 'Newfoundland and Labrador',
    NS: 'Nova Scotia',
    NT: 'Northwest Territories',
    NU: 'Nunavut',
    ON: 'Ontario',
    PE: 'Prince Edward Island',
    QC: 'Quebec',
    SK: 'Saskatchewan',
    YT: 'Yukon'
};

const UNKNOWN_LABEL = 'Unknown';
const HOTSPOT_EXPLORER_HERO_ZOOM = 1;
const HOTSPOT_DETAIL_HERO_ZOOM = 14;
const HOTSPOT_EXPLORER_US_BBOX: [number, number, number, number] = [-179.1489, 18.9103, -66.9326, 71.3652];
const HOTSPOT_EXPLORER_CALIFORNIA_BBOX: [number, number, number, number] = [-124.4820030, 32.5295236, -114.1307816, 42.0094990];
const HOTSPOT_EXPLORER_FRESNO_COUNTY_BBOX: [number, number, number, number] = [-120.918224, 35.906899, -118.359057, 37.585502];

function getObservationTimestamp(observation: {date?: string, time?: string}) {
    if (!observation.date) return 0;

    const normalizedTime = observation.time?.trim();
    if (normalizedTime) {
        const parsedDateTime = DateTime.fromISO(`${observation.date}T${normalizedTime}`);
        if (parsedDateTime.isValid) return parsedDateTime.toMillis();
    }

    const parsedDate = DateTime.fromISO(observation.date);
    return parsedDate.isValid ? parsedDate.toMillis() : 0;
}

function getObservationYear(observation: {date?: string, observedYear?: number}) {
    if (Number.isFinite(observation.observedYear)) return observation.observedYear;
    if (!observation.date) return undefined;

    const parsedYear = parseInt(observation.date.split('-')[0], 10);
    return Number.isFinite(parsedYear) ? parsedYear : undefined;
}

function getObservationMonth(observation: {date?: string, observedMonth?: number}) {
    if (Number.isFinite(observation.observedMonth)) return observation.observedMonth;
    if (!observation.date) return undefined;

    const parsedMonth = parseInt(observation.date.split('-')[1], 10);
    return Number.isFinite(parsedMonth) ? parsedMonth : undefined;
}

export function getHotspotExplorerPeriodFilter(searchParams: URLSearchParams): HotspotExplorerPeriodFilter {
    const rawYear = searchParams.get('year');
    const rawMonth = searchParams.get('month');
    const year = rawYear && rawYear !== 'life' && rawYear !== 'null' ? parseInt(rawYear, 10) : undefined;
    const month = rawMonth && rawMonth !== 'null' ? parseInt(rawMonth, 10) : undefined;

    return {
        year: Number.isFinite(year) ? year : undefined,
        month: Number.isFinite(month) ? month : undefined
    };
}

export function formatHotspotExplorerPeriodLabel(periodFilter?: HotspotExplorerPeriodFilter) {
    if (!periodFilter?.year && !periodFilter?.month) return undefined;

    const monthLabel = periodFilter.month
        ? BASE_MONTHS.find(month => month.value === periodFilter.month)?.label
        : undefined;

    return [monthLabel, periodFilter.year].filter(Boolean).join(' ');
}

function filterHotspotObservationsByPeriod(
    observations: HotspotSource['observations'],
    periodFilter?: HotspotExplorerPeriodFilter
) {
    if (!periodFilter?.year && !periodFilter?.month) return observations;

    return observations.filter(observation => {
        if (periodFilter.year && getObservationYear(observation) !== periodFilter.year) return false;
        if (!periodFilter.month) return true;

        return getObservationMonth(observation) === periodFilter.month;
    });
}

export function getCountryCode(stateProvince?: string) {
    return stateProvince?.split('-')[0] || 'UNK';
}

export function getCountryLabel(countryCode: string) {
    if (countryCode === 'UNK') return UNKNOWN_LABEL;
    return countryDisplayNames?.of(countryCode) ?? countryCode;
}

export function getStateProvinceLabel(stateProvince?: string) {
    if (!stateProvince) return UNKNOWN_LABEL;

    const [countryCode, regionCode] = stateProvince.split('-');
    if (!regionCode) return stateProvince;

    if (countryCode === 'US') {
        return US_STATE_LABELS[regionCode] ?? regionCode;
    }

    if (countryCode === 'CA') {
        return CANADA_PROVINCE_LABELS[regionCode] ?? regionCode;
    }

    return stateProvince;
}

function isRegionCodeLikeLabel(value?: string) {
    return Boolean(value && /^[A-Z]{2}(?:-[A-Z0-9]{1,5})+$/.test(value));
}

function getRegionDivisionLabel(
    label: string | undefined,
    division: {code?: string, name?: string, shortName?: string} | null | undefined,
    fallbackCode?: string
) {
    if (division?.shortName && division.shortName !== division.code) return division.shortName;
    if (division?.name && division.name !== division.code) return division.name;
    if (label && label !== fallbackCode && !isRegionCodeLikeLabel(label)) return label;

    return undefined;
}

function getStateRegionLabel(regionData: LocationRegionData | null | undefined, stateProvince: string) {
    return getRegionDivisionLabel(regionData?.labels?.state, regionData?.subnational1, stateProvince)
        || (
            regionData?.subnational2?.parentCode === stateProvince && regionData.subnational2.parentName
                ? regionData.subnational2.parentName
                : undefined
        )
        || getStateProvinceLabel(stateProvince);
}

export function getHotspotExplorerLocations(
    locations: HotspotSource[],
    periodFilter?: HotspotExplorerPeriodFilter
): HotspotExplorerHotspot[] {
    return locations
        .filter(location => location.locationId && Array.isArray(location.observations) && location.observations.length > 0)
        .map(location => ({
            location,
            observations: filterHotspotObservationsByPeriod(location.observations, periodFilter)
        }))
        .filter(({observations}) => observations.length > 0)
        .map(({location, observations: periodObservations}) => {
            const observations = [...periodObservations];
            const uniqueVisitIds = new Set(
                observations.map(observation => observation.submissionId ?? observation.submissionID).filter(Boolean)
            );
            const uniqueSpecies = new Set(observations.map(observation => observation.taxonomicOrder).filter(Boolean));
            const lastObservation = observations.reduce((latest, observation) => {
                return getObservationTimestamp(observation) > getObservationTimestamp(latest) ? observation : latest;
            });
            const regionData = 'regionData' in location ? location.regionData : undefined;
            const habitatFeedbackSummary = 'habitatFeedbackSummary' in location ? location.habitatFeedbackSummary : undefined;
            const habitatOverride = 'habitatOverride' in location ? location.habitatOverride : undefined;
            const habitatTrustSignals = 'habitatTrustSignals' in location ? location.habitatTrustSignals : undefined;
            const habitatProfile = 'habitatProfile' in location ? location.habitatProfile : undefined;
            const countryCode = regionData?.country?.code || getCountryCode(observations[0]?.stateProvince);
            const stateProvince = regionData?.subnational1?.code || observations[0]?.stateProvince || 'Unknown';
            const county = regionData?.subnational2?.shortName || observations[0]?.county || 'Unknown';
            const stateLabel = getStateRegionLabel(regionData, stateProvince);
            const countyLabel = getRegionDivisionLabel(regionData?.labels?.county, regionData?.subnational2, county)
                || county;

            return {
                locationId: location.locationId,
                location: location.location,
                county,
                stateProvince,
                countryCode,
                countryLabel: regionData?.country?.shortName || getCountryLabel(countryCode),
                stateLabel,
                countyLabel,
                visits: uniqueVisitIds.size,
                speciesSeen: uniqueSpecies.size,
                lastVisited: formatHotspotObservationDay(lastObservation?.date),
                lastVisitedMs: getObservationTimestamp(lastObservation)
                    || observations.reduce((max, observation) => Math.max(max, getObservationTimestamp(observation)), 0),
                observations,
                ecoData: 'ecoData' in location ? location.ecoData : undefined,
                regionData,
                habitatFeedbackSummary,
                habitatOverride,
                habitatTrustSignals,
                habitatProfile
            };
        })
        .sort((a, b) => a.location.localeCompare(b.location));
}

export function getHotspotGroups(
    hotspots: HotspotExplorerHotspot[],
    level: 'country' | 'state' | 'county',
    path: {countryCode?: string, stateProvince?: string} = {}
): HotspotExplorerGroup[] {
    const groups = new Map<string, HotspotExplorerGroup>();

    for (const hotspot of hotspots) {
        if (path.countryCode && hotspot.countryCode !== path.countryCode) continue;
        if (path.stateProvince && hotspot.stateProvince !== path.stateProvince) continue;

        let code = '';
        let label = '';

        if (level === 'country') {
            code = hotspot.countryCode;
            label = hotspot.countryLabel;
        } else if (level === 'state') {
            code = hotspot.stateProvince;
            label = hotspot.stateLabel;
        } else {
            code = hotspot.county;
            label = hotspot.countyLabel;
        }

        if (level !== 'country' && (!code || code === UNKNOWN_LABEL || label === UNKNOWN_LABEL)) {
            continue;
        }

        const existing = groups.get(code);
        if (existing) {
            existing.count += 1;
            continue;
        }

        groups.set(code, {code, label, count: 1});
    }

    return Array.from(groups.values()).sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.label.localeCompare(b.label);
    });
}

export function getVisibleHotspots(
    hotspots: HotspotExplorerHotspot[],
    path: {countryCode?: string, stateProvince?: string, county?: string}
) {
    return hotspots.filter(hotspot => {
        if (path.countryCode && hotspot.countryCode !== path.countryCode) return false;
        if (path.stateProvince && hotspot.stateProvince !== path.stateProvince) return false;
        if (path.county && hotspot.county !== path.county) return false;
        return true;
    });
}

export function getHotspotExplorerPage(searchParams: URLSearchParams | undefined | null) {
    const rawPage = searchParams?.get(HOTSPOT_EXPLORER_PAGE_PARAM);
    const parsedPage = rawPage ? Number.parseInt(rawPage, 10) : 1;

    return Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
}

export function buildHotspotExplorerSearch(searchParams: URLSearchParams, overrides: {page?: number} = {}) {
    const nextParams = new URLSearchParams(searchParams);

    if (overrides.page !== undefined) {
        if (overrides.page > 1) {
            nextParams.set(HOTSPOT_EXPLORER_PAGE_PARAM, String(overrides.page));
        } else {
            nextParams.delete(HOTSPOT_EXPLORER_PAGE_PARAM);
        }
    }

    const nextSearch = nextParams.toString();
    return nextSearch ? `?${nextSearch}` : '';
}

export function getHotspotExplorerPageSlice<T>(items: T[], currentPage: number, pageSize: number) {
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
    const safePage = Math.min(Math.max(1, currentPage), totalPages);
    const startIndex = (safePage - 1) * pageSize;

    return {
        totalPages,
        currentPage: safePage,
        startIndex,
        endIndex: Math.min(startIndex + pageSize, items.length),
        items: items.slice(startIndex, startIndex + pageSize)
    };
}

export function sortHotspots(hotspots: HotspotExplorerHotspot[], sortMode: HotspotExplorerSortMode) {
    const sorted = [...hotspots];

    switch (sortMode) {
        case 'species':
            return sorted.sort((a, b) => b.speciesSeen - a.speciesSeen || b.visits - a.visits || a.location.localeCompare(b.location));
        case 'recent':
            return sorted.sort((a, b) => b.lastVisitedMs - a.lastVisitedMs || b.visits - a.visits || a.location.localeCompare(b.location));
        case 'visits':
        default:
            return sorted.sort((a, b) => b.visits - a.visits || b.speciesSeen - a.speciesSeen || a.location.localeCompare(b.location));
    }
}

export function getCachedSortedHotspots(hotspots: HotspotExplorerHotspot[], sortMode: HotspotExplorerSortMode) {
    const cachedBySortMode = hotspotSortCache.get(hotspots);
    const cached = cachedBySortMode?.[sortMode];
    if (cached) return cached;

    const computed = sortHotspots(hotspots, sortMode);
    hotspotSortCache.set(hotspots, {
        ...(cachedBySortMode ?? {}),
        [sortMode]: computed
    });
    return computed;
}

export function getHotspotExplorerStats(hotspots: HotspotExplorerHotspot[], childLabel: string) {
    const countries = new Set(hotspots.map(hotspot => hotspot.countryCode));
    const counties = new Set(hotspots.map(hotspot => `${hotspot.countryCode}|${hotspot.stateProvince}|${hotspot.county}`));
    const species = new Set<number>();
    const years = new Set<number>();

    for (const hotspot of hotspots) {
        for (const observation of hotspot.observations) {
            species.add(observation.taxonomicOrder);
            if (!observation.date) continue;
            const year = parseInt(observation.date.split('-')[0]);
            if (!Number.isNaN(year)) {
                years.add(year);
            }
        }
    }

    return {
        childLabel,
        childCount: childLabel === 'Countries' ? countries.size : childLabel === 'Counties' ? counties.size : hotspots.length,
        hotspotsVisited: hotspots.length,
        speciesSeen: species.size,
        yearsExploring: years.size
    };
}

export function getCachedHotspotExplorerStats(hotspots: HotspotExplorerHotspot[], childLabel: string) {
    const cacheKey = childLabel || '__default__';
    const cachedByLabel = hotspotStatsCache.get(hotspots);
    const cached = cachedByLabel?.[cacheKey];
    if (cached) return cached;

    const computed = getHotspotExplorerStats(hotspots, childLabel);
    hotspotStatsCache.set(hotspots, {
        ...(cachedByLabel ?? {}),
        [cacheKey]: computed
    });
    return computed;
}

export function getCountryCodeLabel(countryCode: string) {
    return getCountryLabel(countryCode);
}

export function isUnknownHotspotLabel(value?: string) {
    return !value || value === UNKNOWN_LABEL;
}

export function formatHotspotLocationLabel(hotspot: HotspotExplorerHotspot) {
    return [hotspot.countyLabel, hotspot.stateLabel, hotspot.countryLabel]
        .filter(label => !isUnknownHotspotLabel(label))
        .join(', ');
}

export function formatHotspotSubtitle(hotspot: HotspotExplorerHotspot) {
    return isUnknownHotspotLabel(hotspot.countyLabel) ? '' : hotspot.countyLabel;
}

type HotspotExplorerHeroImageContext = {
    routeLevel?: 'landing' | 'country' | 'state' | 'county',
    homeLocation?: {latitude: number, longitude: number},
    regionBounds?: EbirdRegionBounds,
    hotspotLocation?: {latitude: number, longitude: number},
};

function buildStaticMapUrl(pathSegment: string) {
    return `https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/static/${pathSegment}/640x360?access_token=${MAPBOX_ACCESS_TOKEN}`;
}

function buildBboxPathSegment(bbox: [number, number, number, number]) {
    return `[${bbox.join(',')}]`;
}

function buildRegionBoundsPathSegment(bounds: EbirdRegionBounds) {
    return buildBboxPathSegment([bounds.minX, bounds.minY, bounds.maxX, bounds.maxY]);
}

function buildCenterPathSegment(latitude: number, longitude: number, zoom: number) {
    return `${longitude},${latitude},${zoom}`;
}

export function getHotspotExplorerHeroImage(context: HotspotExplorerHeroImageContext = {}) {
    if (context.regionBounds) {
        return buildStaticMapUrl(buildRegionBoundsPathSegment(context.regionBounds));
    }

    if (context.hotspotLocation) {
        return buildStaticMapUrl(
            buildCenterPathSegment(
                context.hotspotLocation.latitude,
                context.hotspotLocation.longitude,
                HOTSPOT_DETAIL_HERO_ZOOM
            )
        );
    }

    if (context.routeLevel === 'county') {
        return buildStaticMapUrl(buildBboxPathSegment(HOTSPOT_EXPLORER_FRESNO_COUNTY_BBOX));
    }

    if (context.routeLevel === 'state') {
        return buildStaticMapUrl(buildBboxPathSegment(HOTSPOT_EXPLORER_CALIFORNIA_BBOX));
    }

    if (context.routeLevel === 'country') {
        return buildStaticMapUrl(buildBboxPathSegment(HOTSPOT_EXPLORER_US_BBOX));
    }

    const resolvedHomeLocation = context.homeLocation && Number.isFinite(context.homeLocation.latitude) && Number.isFinite(context.homeLocation.longitude)
        ? context.homeLocation
        : {latitude: DEFAULT_MAP_CENTER[1], longitude: DEFAULT_MAP_CENTER[0]};

    return buildStaticMapUrl(buildCenterPathSegment(resolvedHomeLocation.latitude, resolvedHomeLocation.longitude, HOTSPOT_EXPLORER_HERO_ZOOM));
}

export function getHotspotDetailHeroImage(hotspotLocation?: {latitude: number, longitude: number}) {
    return getHotspotExplorerHeroImage({hotspotLocation, routeLevel: 'county'});
}

export function buildHotspotExplorerIndex(locations: HotspotSource[], periodFilter?: HotspotExplorerPeriodFilter): HotspotExplorerIndex {
    const hotspots = getHotspotExplorerLocations(locations, periodFilter);
    const hotspotsByLocationId = new Map<string, HotspotExplorerHotspot>();
    const countryGroups = getHotspotGroups(hotspots, 'country');
    const hotspotsByCountryCode = new Map<string, HotspotExplorerHotspot[]>();
    const hotspotsByStateKey = new Map<string, HotspotExplorerHotspot[]>();
    const hotspotsByCountyKey = new Map<string, HotspotExplorerHotspot[]>();
    const stateGroupsByCountryCode = new Map<string, HotspotExplorerGroup[]>();
    const countyGroupsByStateKey = new Map<string, HotspotExplorerGroup[]>();

    for (const hotspot of hotspots) {
        hotspotsByLocationId.set(hotspot.locationId, hotspot);

        const countryList = hotspotsByCountryCode.get(hotspot.countryCode) ?? [];
        countryList.push(hotspot);
        hotspotsByCountryCode.set(hotspot.countryCode, countryList);

        const stateKey = `${hotspot.countryCode}|${hotspot.stateProvince}`;
        const stateList = hotspotsByStateKey.get(stateKey) ?? [];
        stateList.push(hotspot);
        hotspotsByStateKey.set(stateKey, stateList);

        const countyKey = `${hotspot.countryCode}|${hotspot.stateProvince}|${hotspot.county}`;
        const countyList = hotspotsByCountyKey.get(countyKey) ?? [];
        countyList.push(hotspot);
        hotspotsByCountyKey.set(countyKey, countyList);
    }

    for (const countryGroup of countryGroups) {
        const countryHotspots = hotspotsByCountryCode.get(countryGroup.code) ?? [];
        const stateGroups = getHotspotGroups(hotspots, 'state', {countryCode: countryGroup.code});
        stateGroupsByCountryCode.set(countryGroup.code, stateGroups);

        for (const stateGroup of stateGroups) {
            const countyGroups = getHotspotGroups(hotspots, 'county', {
                countryCode: countryGroup.code,
                stateProvince: stateGroup.code
            });
            countyGroupsByStateKey.set(`${countryGroup.code}|${stateGroup.code}`, countyGroups);
        }

        hotspotsByCountryCode.set(countryGroup.code, countryHotspots.sort((a, b) => a.location.localeCompare(b.location)));
    }

    for (const [stateKey, stateHotspots] of Array.from(hotspotsByStateKey.entries())) {
        hotspotsByStateKey.set(stateKey, stateHotspots.sort((a, b) => a.location.localeCompare(b.location)));
    }

    for (const [countyKey, countyHotspots] of Array.from(hotspotsByCountyKey.entries())) {
        hotspotsByCountyKey.set(countyKey, countyHotspots.sort((a, b) => a.location.localeCompare(b.location)));
    }

    return {
        hotspots,
        hotspotsByLocationId,
        countryGroups,
        hotspotsByCountryCode,
        hotspotsByStateKey,
        hotspotsByCountyKey,
        stateGroupsByCountryCode,
        countyGroupsByStateKey
    };
}

export function buildCountryPath(countryCode: string) {
    return `${HOTSPOT_EXPLORER_ROUTE}/list/${encodeURIComponent(countryCode)}`;
}

export function buildStatePath(countryCode: string, stateProvince: string) {
    return `${HOTSPOT_EXPLORER_ROUTE}/list/${encodeURIComponent(countryCode)}/${encodeURIComponent(stateProvince)}`;
}

export function buildCountyPath(countryCode: string, stateProvince: string, county: string) {
    return `${HOTSPOT_EXPLORER_ROUTE}/list/${encodeURIComponent(countryCode)}/${encodeURIComponent(stateProvince)}/${encodeURIComponent(county)}`;
}

export function buildHotspotDetailPath(locationId: string) {
    return `${HOTSPOT_EXPLORER_ROUTE}/hotspot/${encodeURIComponent(locationId)}`;
}

export function findHotspotByLocationId(hotspots: HotspotExplorerHotspot[], locationId: string) {
    return hotspots.find(hotspot => hotspot.locationId === locationId);
}
